import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';

/** Split "Title — description" (em dash, en dash, or hyphen) into title + detail. */
export function splitFeatureTitleDetail(feature: string): { title: string; detail: string | null } {
    const match = feature.match(/^(.+?)\s+[—–-]\s+(.+)$/);
    if (!match) return { title: feature.trim(), detail: null };
    return { title: match[1].trim(), detail: match[2].trim() };
}

export interface SubscriptionFeatureLabelProps {
    feature: string;
    color?: string;
    titleFontSize?: number | string;
    detailFontSize?: number | string;
    sx?: SxProps<Theme>;
}

/** Bold title; when a dash is present, description sits smaller on the next line. */
export function SubscriptionFeatureLabel({
    feature,
    color = 'rgba(255,255,255,0.88)',
    titleFontSize = 14,
    detailFontSize,
    sx,
}: SubscriptionFeatureLabelProps) {
    const { title, detail } = splitFeatureTitleDetail(feature);
    const detailSize =
        detailFontSize ?? (typeof titleFontSize === 'number' ? Math.max(11, titleFontSize - 2) : '0.75rem');

    return (
        <Box sx={sx}>
            <Typography
                component="span"
                sx={{
                    display: 'block',
                    fontSize: titleFontSize,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color,
                    lineHeight: 1.35,
                }}>
                {title}
            </Typography>
            {detail ? (
                <Typography
                    component="span"
                    sx={{
                        display: 'block',
                        mt: 0.35,
                        fontSize: detailSize,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        color,
                        opacity: 0.72,
                        lineHeight: 1.4,
                    }}>
                    {detail}
                </Typography>
            ) : null}
        </Box>
    );
}
